// Pricing service — the boundary between the UI and the B2C pricing backend.
//
// Order of precedence:
//   1. supabase.rpc('calculate_b2c_price', ...) — the authoritative backend
//      engine (migration 013). Public callers receive a retail-only result.
//   2. Local deterministic engine (src/pricing/engine.js) running on the
//      bundled seed snapshot — used when the pricing tables/RPC are not yet
//      migrated or Supabase is unreachable. Result is flagged
//      source: 'local-fallback' so the UI can treat it conservatively.
//
// Public callers NEVER receive internal data: the RPC only returns
// retailPrice/totalPrice/confidence/requiresReview to anon, and the local
// fallback strips the breakdown for non-debug calls.
import { supabase } from '../config/supabase';
import { calculateDoorPrice } from '../pricing/engine';
import { pricingSeed } from '../pricing/pricingSeed';
import { doorFamilies, configuratorOptions, familyCompatibility } from '../data/configurator';

let catalogCache = null;
let pricingCache = null;

// Families/options/compatibility — public-readable tables; falls back to
// the bundled seed when Supabase is unreachable or unmigrated.
export const loadConfiguratorCatalog = async () => {
  if (catalogCache) return catalogCache;
  try {
    const [families, options, compat] = await Promise.all([
      supabase.from('door_families').select('*').eq('active', true),
      supabase.from('configurator_options').select('*').eq('active', true).order('sort'),
      supabase.from('family_option_compat').select('*')
    ]);
    if (families.error || options.error || compat.error || !families.data?.length) {
      throw new Error('pricing tables unavailable');
    }
    const compatMap = {};
    for (const row of compat.data) {
      (compatMap[row.family_id] = compatMap[row.family_id] || {})[row.option_id] = row.is_default;
    }
    catalogCache = {
      families: families.data.map((f) => ({
        id: f.id, slug: f.slug, name: f.name, nameEt: f.name_et, opening: f.opening,
        leafMm: f.leaf_mm,
        minWidth: f.min_width, maxWidth: f.max_width,
        minHeight: f.min_height, maxHeight: f.max_height,
        refWidth: f.ref_width, refHeight: f.ref_height
      })),
      // DB column is name_et; the UI's pick(o, 'name') reads nameEt
      options: options.data.map((o) => ({ ...o, nameEt: o.name_et })),
      compatibility: compatMap
    };
  } catch {
    catalogCache = {
      families: doorFamilies,
      options: configuratorOptions,
      compatibility: familyCompatibility
    };
  }
  // Retail-safe sidelight catalogue (names/widths/enabled families — no prices).
  // Migration 014; missing function/table → fall back to the bundled seed.
  try {
    const { data: slc, error: slErr } = await supabase.rpc('b2c_sidelight_public_config');
    if (slErr) throw slErr;
    catalogCache.sidelight = {
      families: slc?.families || [],
      widths: slc?.widths || [],
      glass: slc?.glass || [],
      layouts: slc?.layouts || ['FULL_GLASS', 'ONE_HORIZONTAL_DIVIDER', 'TWO_HORIZONTAL_DIVIDERS', 'CUSTOM'],
      limits: slc?.limits || {}
    };
  } catch {
    catalogCache.sidelight = {
      families: Object.keys(pricingSeed.sidelight.families).filter((k) => pricingSeed.sidelight.families[k]),
      widths: pricingSeed.sidelight.widths.filter((x) => x.enabled).map((x) => x.width_mm),
      glass: pricingSeed.sidelight.glass.filter((g) => g.enabled).map((g) => ({ type: g.glass_type, name: g.name, nameEt: g.name_et })),
      layouts: ['FULL_GLASS', 'ONE_HORIZONTAL_DIVIDER', 'TWO_HORIZONTAL_DIVIDERS', 'CUSTOM'],
      limits: {
        min_width_mm: pricingSeed.sidelight.settings.min_width_mm,
        max_width_mm: pricingSeed.sidelight.settings.max_width_mm,
        min_height_mm: pricingSeed.sidelight.settings.min_height_mm,
        max_height_mm: pricingSeed.sidelight.settings.max_height_mm
      }
    };
  }
  return catalogCache;
};

// Full pricing dataset — used by the local fallback engine and the admin
// pricing editor. shape matches the engine `data` contract.
export const loadPricingData = async () => {
  try {
    const pls = await supabase
      .from('price_lists').select('*')
      .eq('scope', 'b2c').eq('is_active', true)
      .lte('effective_from', new Date().toISOString().slice(0, 10))
      .order('version', { ascending: false }).limit(1);
    const priceList = pls.data?.[0];
    if (pls.error || !priceList) throw new Error('no active b2c price list');
    const [tiers, factors, rules, stdOpts, overrides] = await Promise.all([
      supabase.from('b2c_base_price_tiers').select('*').eq('price_list_id', priceList.id),
      supabase.from('b2c_factors').select('*').eq('price_list_id', priceList.id).order('sort'),
      supabase.from('b2c_option_rules').select('*').eq('price_list_id', priceList.id),
      supabase.from('b2c_standard_options').select('option_id').eq('price_list_id', priceList.id),
      supabase.from('b2c_retail_overrides').select('*').eq('price_list_id', priceList.id)
    ]);
    if (tiers.error || factors.error || rules.error || stdOpts.error || overrides.error) {
      throw new Error('b2c pricing tables unavailable');
    }
    // Sidelight dataset (migration 014) — failure leaves an empty set, which
    // makes sidelight selections safely unpriced rather than crashing.
    const [slFam, slBase, slGlass, slDiv, slW, slSet] = await Promise.all([
      supabase.from('b2c_sidelight_families').select('*').eq('price_list_id', priceList.id),
      supabase.from('b2c_sidelight_base').select('*').eq('price_list_id', priceList.id),
      supabase.from('b2c_sidelight_glass').select('*').eq('price_list_id', priceList.id).order('sort'),
      supabase.from('b2c_sidelight_dividers').select('*').eq('price_list_id', priceList.id),
      supabase.from('b2c_sidelight_widths').select('*').eq('price_list_id', priceList.id).order('sort'),
      supabase.from('b2c_sidelight_settings').select('*').eq('price_list_id', priceList.id)
    ]);
    const slErr = slFam.error || slBase.error || slGlass.error || slDiv.error || slW.error || slSet.error;
    return {
      families: (catalogCache || await loadConfiguratorCatalog()).families,
      priceList,
      tiers: tiers.data,
      factors: factors.data,
      rules: rules.data,
      standardOptions: stdOpts.data.map((r) => r.option_id),
      overrides: overrides.data,
      sidelight: slErr ? pricingSeed.sidelight : {
        families: Object.fromEntries((slFam.data || []).map((r) => [r.family_id, r.enabled])),
        base: slBase.data || [],
        glass: slGlass.data || [],
        dividers: slDiv.data || [],
        widths: slW.data || [],
        settings: Object.fromEntries((slSet.data || []).map((r) => [r.key, r.value]))
      }
    };
  } catch {
    return { ...pricingSeed, families: (catalogCache || await loadConfiguratorCatalog()).families };
  }
};

// input — see engine.js contract. debug=true only returns a breakdown when
// the caller is an authenticated admin (enforced inside the RPC).
export const calculatePrice = async (input, { debug = false } = {}) => {
  try {
    const { data, error } = await supabase.rpc('calculate_b2c_price', {
      p_input: input,
      p_debug: debug
    });
    if (error) throw error;
    return { ...data, source: 'backend' };
  } catch {
    const data = pricingCache || await loadPricingData();
    const local = calculateDoorPrice(input, data);
    // Never leak the local breakdown to public callers either.
    if (debug) return { ...local, source: 'local-fallback' };
    const { breakdown, subtotalNet, ...publicResult } = local;
    return { ...publicResult, source: 'local-fallback' };
  }
};

// "From €X" for product pages — the public unit price of the family default
// configuration at qty 1. Returns null when the family is unpriced.
export const getFromPrice = async (family) => {
  const input = {
    family: family.id, qty: 1,
    width_mm: family.refWidth, height_mm: family.refHeight,
    opening: family.opening === 'both' ? 'outward' : family.opening
  };
  const res = await calculatePrice(input);
  return res?.retailPrice != null && !res.requiresReview ? res.retailPrice : null;
};

// Persist an immutable snapshot of a quoted price (best-effort; the table may
// not exist yet — failure never blocks the order flow).
export const savePriceSnapshot = async (input, result) => {
  try {
    await supabase.from('quote_price_snapshots').insert({
      input,
      price_list_code: result?.priceList?.code ?? null,
      price_list_ver: result?.priceList?.version ?? null,
      result,
      confidence: result?.confidence ?? null,
      requires_review: result?.requiresReview ?? true
    });
  } catch {
    // pricing tables not migrated yet — safe to skip
  }
};

// --- Admin helpers ----------------------------------------------------------

const audit = async (entries) => {
  try {
    const rows = entries.map((e) => ({ ...e, changed_at: new Date().toISOString() }));
    await supabase.from('b2c_pricing_audit').insert(rows);
  } catch { /* audit is best-effort */ }
};

export const saveBasePriceTiers = async (priceListId, rows, previous) => {
  const clean = rows.map(({ id, family_id, variant, qty_min, qty_max, price, data_status }) => ({
    ...(typeof id === 'number' ? { id } : {}),
    price_list_id: priceListId, family_id, variant,
    qty_min, qty_max, price, data_status: data_status || 'verified'
  }));
  const { error } = await supabase.from('b2c_base_price_tiers')
    .upsert(clean, { onConflict: 'price_list_id,family_id,variant,qty_min' });
  if (!error) {
    await audit(clean.map((r) => ({
      table_name: 'b2c_base_price_tiers',
      row_key: `${r.family_id}/${r.variant}/${r.qty_min}`,
      old_row: previous?.find((p) => p.family_id === r.family_id && p.variant === r.variant && p.qty_min === r.qty_min) || null,
      new_row: r
    })));
  }
  return { error };
};

export const saveFactors = async (priceListId, rows, previous) => {
  const clean = rows.map(({ id, scope, scope_key, operation, value, label, enabled, sort }) => ({
    ...(typeof id === 'number' ? { id } : {}),
    price_list_id: priceListId, scope, scope_key, operation, value, label, enabled, sort
  }));
  const updates = clean.filter((r) => r.id);
  const inserts = clean.filter((r) => !r.id);
  let error = null;
  if (updates.length) ({ error } = await supabase.from('b2c_factors').upsert(updates));
  if (!error && inserts.length) ({ error } = await supabase.from('b2c_factors').insert(inserts));
  if (!error) {
    await audit(clean.map((r) => ({
      table_name: 'b2c_factors', row_key: `${r.scope}/${r.scope_key || ''}`,
      old_row: previous?.find((p) => p.id === r.id) || null, new_row: r
    })));
  }
  return { error };
};

export const saveOptionRules = async (priceListId, rows, previous) => {
  const clean = rows.map(({ id, family_id, rule_kind, target, meta, qty_min, qty_max, op, value, unit, label, data_status, charge_scope, component_scope }) => ({
    ...(typeof id === 'number' ? { id } : {}),
    price_list_id: priceListId, family_id, rule_kind, target, meta,
    qty_min, qty_max, op, value, unit, label, data_status: data_status || 'verified',
    charge_scope: charge_scope || 'PER_DOOR_LEAF',
    component_scope: component_scope || 'DOOR'
  }));
  const updates = clean.filter((r) => r.id);
  const inserts = clean.filter((r) => !r.id);
  let error = null;
  if (updates.length) ({ error } = await supabase.from('b2c_option_rules').upsert(updates));
  if (!error && inserts.length) ({ error } = await supabase.from('b2c_option_rules').insert(inserts));
  if (!error) {
    await audit(clean.map((r) => ({
      table_name: 'b2c_option_rules', row_key: `${r.target}/${r.qty_min ?? 'any'}`,
      old_row: previous?.find((p) => p.id === r.id) || null, new_row: r
    })));
  }
  return { error };
};

export const saveStandardOptions = async (priceListId, optionIds, previous) => {
  const { error: delError } = await supabase.from('b2c_standard_options')
    .delete().eq('price_list_id', priceListId);
  if (delError) return { error: delError };
  const rows = optionIds.map((option_id) => ({ price_list_id: priceListId, option_id }));
  const { error } = rows.length
    ? await supabase.from('b2c_standard_options').insert(rows)
    : { error: null };
  if (!error) {
    await audit([{ table_name: 'b2c_standard_options', row_key: 'set', old_row: previous, new_row: optionIds }]);
  }
  return { error };
};

export const saveRetailOverride = async (priceListId, familyId, variant, qtyMin, qtyMax, price, note, previous) => {
  // Deactivate the previous active override for the same scope (audit trail).
  await supabase.from('b2c_retail_overrides')
    .update({ active: false })
    .eq('price_list_id', priceListId).eq('family_id', familyId).eq('variant', variant)
    .eq('active', true);
  const { error } = await supabase.from('b2c_retail_overrides').insert({
    price_list_id: priceListId, family_id: familyId, variant,
    qty_min: qtyMin, qty_max: qtyMax, price, note, active: true
  });
  if (!error) {
    await audit([{ table_name: 'b2c_retail_overrides', row_key: `${familyId}/${variant}`, old_row: previous || null, new_row: { price, qty_min: qtyMin, qty_max: qtyMax, note } }]);
  }
  return { error };
};

export const savePriceListSettings = async (priceListId, roundingMode, roundingStep, previous) => {
  const { error } = await supabase.from('price_lists')
    .update({ rounding_mode: roundingMode, rounding_step: roundingStep })
    .eq('id', priceListId);
  if (!error) {
    await audit([{ table_name: 'price_lists', row_key: String(priceListId), old_row: previous || null, new_row: { rounding_mode: roundingMode, rounding_step: roundingStep } }]);
  }
  return { error };
};

// --- Sidelight admin helpers (migration 014) --------------------------------

// Generic upsert for the simple sidelight tables; `keyCols` makes the
// audit row_key and (where the table has one) the conflict target.
const saveSidelightRows = async (table, rows, previous, keyCols, onConflict) => {
  const clean = rows.map((r) => {
    const { id, ...rest } = r;
    return { ...(typeof id === 'number' ? { id } : {}), ...rest };
  });
  const updates = clean.filter((r) => typeof r.id === 'number');
  const inserts = clean.filter((r) => typeof r.id !== 'number');
  let error = null;
  if (onConflict) {
    // Composite-key tables (or upsert-by-natural-key) — upsert everything.
    ({ error } = await supabase.from(table).upsert(clean, { onConflict }));
  } else {
    if (updates.length) ({ error } = await supabase.from(table).upsert(updates));
    if (!error && inserts.length) ({ error } = await supabase.from(table).insert(inserts));
  }
  if (!error) {
    await audit(clean.map((r) => ({
      table_name: table,
      row_key: keyCols.map((k) => r[k] ?? '').join('/'),
      old_row: (previous || []).find((p) => p.id === r.id) || null,
      new_row: r
    })));
  }
  return { error };
};

export const saveSidelightFamilies = async (priceListId, rows, previous) =>
  saveSidelightRows('b2c_sidelight_families',
    rows.map((r) => ({ price_list_id: priceListId, family_id: r.family_id, enabled: !!r.enabled })),
    previous, ['family_id'], 'price_list_id,family_id');

export const saveSidelightBase = async (priceListId, rows, previous) =>
  saveSidelightRows('b2c_sidelight_base',
    rows.map(({ id, family_id, variant, base_price, enabled, valid_from, valid_to, data_status }) => ({
      ...(typeof id === 'number' ? { id } : {}),
      price_list_id: priceListId, family_id: family_id || null,
      variant: variant || 'default', base_price, enabled: enabled !== false,
      valid_from: valid_from || null, valid_to: valid_to || null,
      data_status: base_price != null ? (data_status === 'placeholder' ? 'verified' : data_status || 'verified') : 'placeholder'
    })),
    previous, ['family_id', 'variant']);

export const saveSidelightGlass = async (priceListId, rows, previous) =>
  saveSidelightRows('b2c_sidelight_glass',
    rows.map(({ id, glass_type, name, name_et, price_per_m2, enabled, families, sort }) => ({
      ...(typeof id === 'number' ? { id } : {}),
      price_list_id: priceListId, glass_type, name, name_et, price_per_m2,
      enabled: enabled !== false, families: families || null, sort: sort || 0
    })),
    previous, ['glass_type'], 'price_list_id,glass_type');

export const saveSidelightDividers = async (priceListId, rows, previous) =>
  saveSidelightRows('b2c_sidelight_dividers',
    rows.map(({ id, divider_type, name, fixed_price, price_per_lm, enabled }) => ({
      ...(typeof id === 'number' ? { id } : {}),
      price_list_id: priceListId, divider_type, name, fixed_price, price_per_lm,
      enabled: enabled !== false
    })),
    previous, ['divider_type'], 'price_list_id,divider_type');

export const saveSidelightWidths = async (priceListId, rows, previous) =>
  saveSidelightRows('b2c_sidelight_widths',
    rows.map(({ id, width_mm, enabled, sort }) => ({
      ...(typeof id === 'number' ? { id } : {}),
      price_list_id: priceListId, width_mm, enabled: enabled !== false, sort: sort || 0
    })),
    previous, ['width_mm'], 'price_list_id,width_mm');

export const saveSidelightSettings = async (priceListId, settings, previous) => {
  const rows = Object.entries(settings).map(([key, value]) => ({
    price_list_id: priceListId, key, value
  }));
  const { error } = await supabase.from('b2c_sidelight_settings')
    .upsert(rows, { onConflict: 'price_list_id,key' });
  if (!error) {
    await audit([{ table_name: 'b2c_sidelight_settings', row_key: 'set', old_row: previous, new_row: settings }]);
  }
  return { error };
};

export const invalidatePricingCache = () => { pricingCache = null; };
export const setPricingCache = (data) => { pricingCache = data; };
